import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type {
  BrandMediaAspectRatio,
  BrandMediaGenerateInput,
  BrandMediaItem,
  BrandMediaListResponse,
  BrandMediaResolution,
} from "@/types/brandMedia.type";

export const brandMediaKey = (brandId: string | null) => ["brandMedia", brandId];

export const BRAND_MEDIA_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export const MAX_BRAND_MEDIA_BYTES = 15 * 1024 * 1024;

/**
 * Mirrors the backend's gpt-image-2 size rules (gptImage.ts): edges snap to a
 * 16px grid within 256-3840, edge ratio at most 3:1. Kept in sync by hand so
 * the picker can preview the exact pixels a preset or custom size produces.
 */
export const GENERATION_MIN_EDGE = 256;
export const GENERATION_MAX_EDGE = 3840;
export const GENERATION_EDGE_STEP = 16;
export const GENERATION_MAX_RATIO = 3;

const GENERATION_SHORT_EDGE: Record<BrandMediaResolution, number> = {
  "1K": 1024,
  "2K": 2048,
};

export function snapGenerationEdge(value: number): number {
  const snapped = Math.round(value / GENERATION_EDGE_STEP) * GENERATION_EDGE_STEP;
  return Math.min(GENERATION_MAX_EDGE, Math.max(GENERATION_MIN_EDGE, snapped));
}

export function generationPresetSize(
  aspectRatio: BrandMediaAspectRatio,
  resolution: BrandMediaResolution,
): { width: number; height: number } {
  const [w, h] = aspectRatio.split(":").map(Number);
  const shortEdge = GENERATION_SHORT_EDGE[resolution];
  if (w >= h) {
    return { width: snapGenerationEdge((shortEdge * w) / h), height: shortEdge };
  }
  return { width: shortEdge, height: snapGenerationEdge((shortEdge * h) / w) };
}

/** Matches the backend list cap (`MAX_PAGE_SIZE`). The library page uses this
 *  so one request fills a wall; pickers keep the smaller default. */
export const BRAND_MEDIA_LIBRARY_LIMIT = 60;

type BrandMediaListOpts = {
  page?: number;
  limit?: number;
};

type UploadUrlResponse = {
  mediaId: string;
  uploadUrl: string;
  uploadToken: string;
};

export function extensionOf(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : "";
}

export function imageMimeFor(file: File): string {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".gif")) return "image/gif";
  return "image/jpeg";
}

/** True for the image types the media pipeline accepts. */
export function isImageFile(file: File): boolean {
  if (file.type) return BRAND_MEDIA_ACCEPT.split(",").includes(file.type);
  return /\.(png|jpe?g|webp|gif)$/i.test(file.name);
}

/**
 * Pixel dimensions are only readable in the browser, so they are decoded here
 * and sent along with the confirm. Resolves undefined when the browser cannot
 * decode the file — the row is still created, just without dimensions.
 */
export function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    const done = (value: { width: number; height: number } | undefined) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    img.onload = () =>
      done(
        img.naturalWidth && img.naturalHeight
          ? { width: img.naturalWidth, height: img.naturalHeight }
          : undefined,
      );
    img.onerror = () => done(undefined);
    img.src = url;
  });
}

/**
 * The brand's media library — this brand only, never the organization.
 *
 * Never polled: every row carries a presigned URL that a refetch would rotate,
 * which restarts image loads for no reason. Uploads and generations invalidate
 * the key themselves, and nothing else writes to this list.
 */
export function useBrandMedia(brandId: string | null, opts?: BrandMediaListOpts) {
  const page = opts?.page ?? 1;
  const limit = opts?.limit ?? 24;
  return useQuery({
    queryKey: [...brandMediaKey(brandId), page, limit],
    queryFn: async () => {
      const res = await axiosInstance.get<BrandMediaListResponse>(
        `/brand-media/${brandId}`,
        { params: { page, limit } },
      );
      return res.data;
    },
    enabled: !!brandId,
    // Comfortably inside the 1h presign TTL.
    staleTime: 5 * 60_000,
    // Keep the current page painted while the next one loads — but never
    // carry another brand's rows across a workspace switch.
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery?.queryKey[1] === brandId) return previousData;
      return undefined;
    },
  });
}

export function useBrandMediaMutations(brandId: string | null) {
  const qc = useQueryClient();

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: brandMediaKey(brandId) }),
    [qc, brandId],
  );

  const upload = useMutation({
    mutationFn: async (file: File): Promise<BrandMediaItem> => {
      if (file.size > MAX_BRAND_MEDIA_BYTES) {
        throw new Error(
          `Image is too large (max ${Math.floor(MAX_BRAND_MEDIA_BYTES / (1024 * 1024))} MB)`,
        );
      }

      const mimetype = imageMimeFor(file);
      const dimensions = await readImageDimensions(file);

      const { data } = await axiosInstance.post<UploadUrlResponse>(
        `/brand-media/${brandId}/upload-url`,
        {
          filename: file.name,
          mimetype,
          extension: extensionOf(file.name) || "jpg",
        },
      );

      // The PUT must carry exactly the Content-Type the presign was minted with.
      const putRes = await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": mimetype },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

      const confirmed = await axiosInstance.post<BrandMediaItem>(
        `/brand-media/${brandId}/confirm`,
        { uploadToken: data.uploadToken, ...dimensions },
      );
      return confirmed.data;
    },
    onSuccess: invalidate,
  });

  const generate = useMutation({
    mutationFn: async (input: BrandMediaGenerateInput): Promise<BrandMediaItem> => {
      const { data } = await axiosInstance.post<BrandMediaItem>(
        `/brand-media/${brandId}/generate`,
        input,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (mediaId: string) => {
      const { data } = await axiosInstance.delete(`/brand-media/${brandId}/${mediaId}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { upload, generate, remove };
}
